// Custom Tooltip Component for Charts - Handles dynamic data

import React from 'react';

// Format large numbers
const formatNumber = (value) => {
  if (value === null || value === undefined) return '0';
  if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(2)}K`;
  return value.toLocaleString('en-US', { 
    minimumFractionDigits: 0, 
    maximumFractionDigits: 2 
  });
};

// Format date for better readability
const formatDate = (dateStr) => {
  if (!dateStr) return dateStr;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  const displayLabel = label !== undefined && label !== null ? String(label) : '';
  const isDate = displayLabel.match(/^\d{4}-\d{2}-\d{2}/);

  // Calculate total if multiple series
  const total = payload.reduce((sum, entry) => {
    const value = entry.value;
    if (typeof value === 'number') return sum + value;
    return sum;
  }, 0);

  // Responsive sizing for mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const tooltipPadding = isMobile ? 'p-3' : 'p-4';
  const tooltipMinWidth = isMobile ? 'min-w-[180px]' : 'min-w-[220px]';
  const tooltipMaxWidth = isMobile ? 'max-w-[280px]' : 'max-w-[320px]';
  const fontSize = isMobile ? 'text-xs' : 'text-sm';

  return (
    <div className={`bg-white border-2 border-gray-300 rounded-lg ${tooltipPadding} shadow-2xl ${tooltipMinWidth} ${tooltipMaxWidth} z-50 animate-fade-in-up`}>
      {displayLabel && (
        <p className={`font-bold text-gray-900 mb-2 sm:mb-3 pb-2 border-b border-gray-200 ${fontSize}`}>
          {isDate ? formatDate(displayLabel) : displayLabel}
        </p>
      )}
      <div className="space-y-2 sm:space-y-2.5">
        {payload
          .filter(entry => entry && entry !== null && entry.value !== undefined && entry.value !== null)
          .map((entry, index) => {
            const name = entry.name || entry.dataKey || 'Value';
            const value = entry.value;
            const color = entry.color || '#0A3D62';
            
            let displayValue = 'N/A';
            let rawValue = value;
            
            if (value !== undefined && value !== null) {
              if (typeof value === 'number') {
                rawValue = value;
                displayValue = formatNumber(value);
              } else {
                displayValue = String(value);
                rawValue = parseFloat(value) || 0;
              }
            }

            // Calculate percentage if total is available
            const percentage = total > 0 && typeof rawValue === 'number' 
              ? ((rawValue / total) * 100).toFixed(1) 
              : null;

            return (
              <div key={index} className={`flex items-center justify-between gap-2 sm:gap-3 ${fontSize}`}>
                <div className="flex items-center gap-2 sm:gap-2.5 flex-1 min-w-0">
                  <div 
                    className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} rounded shadow-sm flex-shrink-0`} 
                    style={{ backgroundColor: color }}
                  ></div>
                  <span className="text-gray-700 font-semibold truncate" title={name}>
                    {name}:
                  </span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                  <span className="font-bold text-gray-900">{displayValue}</span>
                  {percentage !== null && payload.length > 1 && (
                    <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-500 font-medium`}>
                      ({percentage}%)
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        {payload.length > 1 && total > 0 && (
          <div className={`pt-2 mt-2 border-t border-gray-200 flex items-center justify-between ${fontSize}`}>
            <span className="text-gray-700 font-semibold">Total:</span>
            <span className="font-bold text-gray-900">{formatNumber(total)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomTooltip;

